import React, { useState, useEffect } from "react";
import { C, ROLE_PERMISSIONS } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, MeetingsTab, TransactionsTab,
  OperationalPlanTab, ReportsTab, useToast
} from "./erp-shared";
import {
  TrendingUp, Users, Plus, Search, CheckCircle, Clock,
  Star, FileText, Calendar, Target, DollarSign, Receipt,
  Phone, Mail, MapPin, Award, Handshake, Rocket,
  BarChart3, Layers, Building, Gift, Flag, Eye,
  ArrowUpRight, Briefcase, Crown, Percent
} from "lucide-react";

/* ═════════════════════════════════════════════
   ✦ Fundraising Page
   ═════════════════════════════════════════════ */
export default function FundraisingPage({ user }) {
  const [tab, setTab] = useState("projects");
  const [triggers, setTriggers] = useState({ partnership: 0 });
  const perms = ROLE_PERMISSIONS[user.role] || {};

  const tabs = [
    { key: "projects", label: "المشاريع", icon: Rocket },
    { key: "partnerships", label: "الشراكات", icon: Handshake },
    { key: "meetings", label: "الاجتماعات", icon: Calendar },
    { key: "transactions", label: "المعاملات المالية", icon: FileText },
    { key: "plan", label: "الخطة التشغيلية", icon: Target },
    { key: "reports", label: "التقارير", icon: FileText },
  ];

  function fireQuick(targetTab, triggerKey) {
    setTab(targetTab);
    setTriggers(prev => ({ ...prev, [triggerKey]: prev[triggerKey] + 1 }));
  }

  return (
    <div>
      <ModuleHeader icon={TrendingUp} title="تنمية الموارد" subtitle="إدارة مشاريع جمع التبرعات والشراكات الاستراتيجية" />

      {perms.canEdit && (
        <div style={fq.row}>
          <button style={fq.btn} onClick={() => fireQuick("partnerships", "partnership")}>
            <Handshake size={16} color="#7c3aed" />
            <span>تأسيس شراكة جديدة</span>
          </button>
        </div>
      )}

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === "projects" && <ProjectsTab canEdit={perms.canEdit} />}
      {tab === "partnerships" && <PartnershipsTab canEdit={perms.canEdit} triggerOpen={triggers.partnership} />}
      {tab === "meetings" && <MeetingsTab departmentName="تنمية الموارد" />}
      {tab === "transactions" && <TransactionsTab budget={450000} />}
      {tab === "plan" && <OperationalPlanTab departmentKey="fundraising" />}
      {tab === "reports" && <ReportsTab departmentName="تنمية الموارد" />}
    </div>
  );
}

const fq = {
  row: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18 },
  btn: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "10px 18px", borderRadius: 10,
    background: C.white, color: C.primary, border: `1px solid ${C.border}`,
    fontFamily: "'Readex Pro', sans-serif", fontSize: 13, fontWeight: 600,
    cursor: "pointer", transition: "all 0.15s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },
};

/* ─────────────────────────────────────────────
   ✦ Projects Tab — مشاريع جمع التبرعات
   ───────────────────────────────────────────── */
const INITIAL_PROJECT = { name: "", category: "", description: "", target: "", startDate: "", endDate: "", responsible: "" };

function ProjectsTab({ canEdit }) {
  const [projects, setProjects] = useState(DEMO_PROJECTS);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_PROJECT);
  const [showToast, ToastView] = useToast();

  function handleSave() {
    if (!form.name || !form.category || !form.description || !form.target || !form.startDate || !form.endDate) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const newP = {
      id: `fp${Date.now()}`,
      name: form.name,
      category: form.category,
      description: form.description,
      target: Number(form.target) || 0,
      collected: 0,
      donors: 0,
      startDate: form.startDate,
      endDate: form.endDate,
      status: "planning",
      responsible: form.responsible || "—",
      color: "#7c3aed",
      notes: null,
    };
    setProjects(prev => [...prev, newP]);
    setForm(INITIAL_PROJECT);
    setShowAdd(false);
    showToast("تم إنشاء المشروع");
  }

  const filtered = projects.filter(p => p.name.includes(search) || p.category.includes(search));

  const PROJ_STATUSES = [
    { key: "active", label: "نشط", color: C.success },
    { key: "planning", label: "تخطيط", color: "#2563eb" },
    { key: "completed", label: "مكتمل", color: C.textLight },
    { key: "paused", label: "متوقف", color: C.warning },
  ];

  const totalTarget = projects.reduce((s, p) => s + p.target, 0);
  const totalCollected = projects.reduce((s, p) => s + p.collected, 0);

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="المشاريع" value={projects.length} icon={Rocket} color="#7c3aed" />
        <KPICard title="المستهدف" value={`${(totalTarget / 1000000).toFixed(1)}M`} icon={Target} color={C.primary} />
        <KPICard title="المحصّل" value={`${(totalCollected / 1000000).toFixed(1)}M`} icon={DollarSign} color={C.success} />
        <KPICard title="نسبة التحصيل" value={`${Math.round((totalCollected / totalTarget) * 100)}%`} icon={Percent} color={C.teal} />
        <KPICard title="نشطة" value={projects.filter(p => p.status === "active").length} icon={CheckCircle} color={C.success} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو الفئة..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">مشروع جديد</Btn>}
        </div>
      </Card>

      <div style={styles.projectsGrid}>
        {filtered.map(p => {
          const pct = Math.round((p.collected / p.target) * 100);
          return (
            <Card key={p.id} style={styles.projectCard} onClick={() => setSelected(p)}>
              <div style={{ ...styles.projectStrip, background: p.color }} />
              <div style={{ padding: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: `${p.color}12`, color: p.color }}>{p.category}</span>
                  <StatusBadge status={p.status} statuses={PROJ_STATUSES} />
                </div>
                <h4 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: C.primary }}>{p.name}</h4>
                <p style={{ margin: 0, fontSize: 13, color: C.textLight, lineHeight: 1.6 }}>{p.description}</p>

                {/* Financial progress */}
                <div style={styles.projectFinance}>
                  <div style={styles.financeRow}>
                    <span style={{ fontSize: 12, color: C.textLight }}>المستهدف</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: C.primary }}>{p.target.toLocaleString()}</span>
                  </div>
                  <div style={styles.financeRow}>
                    <span style={{ fontSize: 12, color: C.textLight }}>المحصّل</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: C.success }}>{p.collected.toLocaleString()}</span>
                  </div>
                </div>

                <div style={{ marginTop: 10 }}>
                  <div style={styles.progressBg}>
                    <div style={{ ...styles.progressFill, width: `${Math.min(pct, 100)}%`, background: pct >= 100 ? C.success : pct >= 60 ? C.teal : C.warning }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                    <span style={{ fontSize: 11, color: C.textLight }}>{p.donors} متبرع</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: pct >= 100 ? C.success : C.teal }}>{pct}%</span>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 10, fontSize: 12, color: C.textLight }}>
                  <span><Calendar size={12} /> {p.startDate}</span>
                  <span>—</span>
                  <span>{p.endDate}</span>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {selected && (
        <Modal title={selected.name} onClose={() => setSelected(null)} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ ...styles.detailBanner, background: `linear-gradient(135deg, ${selected.color}, ${selected.color}CC)` }}>
              <Rocket size={28} color="white" />
              <div>
                <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "white" }}>{selected.name}</p>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: "rgba(255,255,255,0.8)" }}>{selected.category}</p>
              </div>
            </div>
            <div style={styles.detailRow}><strong>الوصف:</strong> {selected.description}</div>
            <div style={styles.detailRow}><strong>الفترة:</strong> {selected.startDate} — {selected.endDate}</div>
            <div style={styles.detailRow}><strong>المستهدف:</strong> {selected.target.toLocaleString()} ريال</div>
            <div style={styles.detailRow}><strong>المحصّل:</strong> <span style={{ color: C.success, fontWeight: 700 }}>{selected.collected.toLocaleString()} ريال</span></div>
            <div style={styles.detailRow}><strong>عدد المتبرعين:</strong> {selected.donors}</div>
            <div style={styles.detailRow}><strong>المسؤول:</strong> {selected.responsible}</div>
            {selected.notes && <div style={styles.detailRow}><strong>ملاحظات:</strong> {selected.notes}</div>}
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إنشاء مشروع جمع تبرعات" onClose={() => { setShowAdd(false); setForm(INITIAL_PROJECT); }} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="اسم المشروع" required placeholder="مثال: مشروع بناء مسجد النور" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <Field label="الفئة" type="select" options={["بناء مساجد","كفالة دعاة","إفطار صائمين","برامج تعليمية","صيانة","مشاريع عامة"]} required value={form.category} onChange={v => setForm({ ...form, category: v })} />
            <Field label="الوصف" type="textarea" required placeholder="وصف المشروع وأهدافه..." rows={3} value={form.description} onChange={v => setForm({ ...form, description: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="المبلغ المستهدف" type="number" required placeholder="0" value={form.target} onChange={v => setForm({ ...form, target: v })} />
              <Field label="تاريخ البداية" type="date" required value={form.startDate} onChange={v => setForm({ ...form, startDate: v })} />
              <Field label="تاريخ النهاية" type="date" required value={form.endDate} onChange={v => setForm({ ...form, endDate: v })} />
            </div>
            <Field label="المسؤول" placeholder="اسم المسؤول" value={form.responsible} onChange={v => setForm({ ...form, responsible: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Rocket} onClick={handleSave}>إنشاء</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_PROJECT); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Partnerships Tab — الشراكات
   ───────────────────────────────────────────── */
const PARTNER_STATUS_LABELS = ["نشطة", "تفاوض", "منتهية"];
const PARTNER_STATUS_LABEL_TO_KEY = { "نشطة": "active", "تفاوض": "negotiation", "منتهية": "expired" };
const INITIAL_PARTNER = {
  name: "", type: "", value: "", scope: "",
  startDate: "", endDate: "",
  contactName: "", contactPhone: "", contactEmail: "",
  benefits: "", specialTerms: "",
  statusLabel: "نشطة", rating: 5, recommendRenewal: "نعم",
  notes: "",
};

function PartnershipsTab({ canEdit, triggerOpen = 0 }) {
  const [partners, setPartners] = useState(DEMO_PARTNERS);
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);
  const [form, setForm] = useState(INITIAL_PARTNER);
  const [showToast, ToastView] = useToast();

  function handleSave() {
    if (!form.name || !form.type || !form.value || !form.startDate || !form.endDate) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const newP = {
      id: `pt${Date.now()}`,
      name: form.name,
      type: form.type,
      scope: form.scope || "",
      value: Number(form.value) || 0,
      startDate: form.startDate,
      endDate: form.endDate,
      status: PARTNER_STATUS_LABEL_TO_KEY[form.statusLabel] || "active",
      contactPerson: form.contactName || "—",
      contactPhone: form.contactPhone,
      contactEmail: form.contactEmail,
      benefitsText: form.benefits,
      specialTerms: form.specialTerms,
      rating: Number(form.rating) || 0,
      recommendRenewal: form.recommendRenewal === "نعم",
      notes: form.notes,
      color: "#0891b2",
      benefits: [],
    };
    setPartners(prev => [...prev, newP]);
    setForm(INITIAL_PARTNER);
    setShowAdd(false);
    showToast("تمت إضافة الشراكة");
  }

  const PARTNER_STATUSES = [
    { key: "active", label: "نشطة", color: C.success },
    { key: "negotiation", label: "تفاوض", color: C.warning },
    { key: "expired", label: "منتهية", color: C.textLight },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="الشراكات" value={partners.length} icon={Handshake} color="#0891b2" />
        <KPICard title="نشطة" value={partners.filter(p => p.status === "active").length} icon={CheckCircle} color={C.success} />
        <KPICard title="قيمة الشراكات" value={`${(partners.reduce((s, p) => s + p.value, 0) / 1000).toFixed(0)}K`} icon={DollarSign} color={C.accent} />
      </div>

      {canEdit && (
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">شراكة جديدة</Btn>
        </div>
      )}

      <div style={styles.partnersGrid}>
        {partners.map(p => (
          <Card key={p.id} style={styles.partnerCard} onClick={() => setSelected(p)}>
            <div style={styles.partnerHeader}>
              <div style={{ ...styles.partnerIcon, background: `${p.color}15` }}>
                <Building size={24} color={p.color} />
              </div>
              <StatusBadge status={p.status} statuses={PARTNER_STATUSES} />
            </div>
            <h4 style={{ margin: "12px 0 4px", fontSize: 16, fontWeight: 700, color: C.primary }}>{p.name}</h4>
            <p style={{ margin: 0, fontSize: 13, color: C.textLight, lineHeight: 1.6 }}>{p.scope}</p>

            <div style={styles.partnerValue}>
              <DollarSign size={16} color={C.success} />
              <span style={{ fontSize: 18, fontWeight: 700, color: C.success }}>{p.value.toLocaleString()}</span>
              <span style={{ fontSize: 12, color: C.textLight }}>ريال</span>
            </div>

            <div style={styles.partnerMeta}>
              <span><Calendar size={12} /> {p.startDate} — {p.endDate}</span>
            </div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 8 }}>
              {p.benefits.slice(0, 3).map((b, i) => <span key={i} style={styles.benefitTag}>{b}</span>)}
            </div>
          </Card>
        ))}
      </div>

      {selected && (
        <Modal title={`شراكة — ${selected.name}`} onClose={() => setSelected(null)} width={550}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={styles.detailRow}><strong>الجهة:</strong> {selected.name}</div>
            <div style={styles.detailRow}><strong>النوع:</strong> {selected.type}</div>
            <div style={styles.detailRow}><strong>النطاق:</strong> {selected.scope}</div>
            <div style={styles.detailRow}><strong>القيمة:</strong> <span style={{ color: C.success, fontWeight: 700 }}>{selected.value.toLocaleString()} ريال</span></div>
            <div style={styles.detailRow}><strong>الفترة:</strong> {selected.startDate} — {selected.endDate}</div>
            <div style={styles.detailRow}><strong>المسؤول:</strong> {selected.contactPerson}</div>
            <div style={styles.detailRow}>
              <strong>المنافع:</strong>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                {selected.benefits.map((b, i) => <span key={i} style={styles.benefitTag}>{b}</span>)}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إضافة شراكة جديدة" onClose={() => { setShowAdd(false); setForm(INITIAL_PARTNER); }} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="اسم الجهة" required placeholder="اسم الشركة أو المؤسسة" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={["راعي رئيسي","راعي ذهبي","راعي فضي","شريك استراتيجي","داعم"]} required value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="القيمة (ريال)" type="number" required placeholder="0" value={form.value} onChange={v => setForm({ ...form, value: v })} />
            </div>
            <Field label="نطاق الشراكة" type="textarea" rows={3} placeholder="وصف نطاق الشراكة والالتزامات..." value={form.scope} onChange={v => setForm({ ...form, scope: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="تاريخ البداية" type="date" required value={form.startDate} onChange={v => setForm({ ...form, startDate: v })} />
              <Field label="تاريخ النهاية" type="date" required value={form.endDate} onChange={v => setForm({ ...form, endDate: v })} />
            </div>

            {/* Contact */}
            <div style={{ padding: 14, background: `${C.teal}06`, borderRadius: 10, border: `1px solid ${C.teal}20` }}>
              <p style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: C.teal }}>جهة الاتصال</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Field label="الاسم" placeholder="اسم المسؤول" value={form.contactName} onChange={v => setForm({ ...form, contactName: v })} />
                <Field label="الهاتف" placeholder="05xxxxxxxx" value={form.contactPhone} onChange={v => setForm({ ...form, contactPhone: v })} />
                <Field label="البريد" placeholder="email@example.com" value={form.contactEmail} onChange={v => setForm({ ...form, contactEmail: v })} />
              </div>
            </div>

            <Field label="المنافع المتبادلة" type="textarea" rows={3} placeholder="منافع الشراكة لكلا الطرفين..." value={form.benefits} onChange={v => setForm({ ...form, benefits: v })} />
            <Field label="شروط خاصة" type="textarea" rows={2} placeholder="أي شروط خاصة..." value={form.specialTerms} onChange={v => setForm({ ...form, specialTerms: v })} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الحالة" type="select" options={PARTNER_STATUS_LABELS} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
              <Field label="هل يُوصى بالتجديد؟" type="select" options={["نعم", "لا"]} value={form.recommendRenewal} onChange={v => setForm({ ...form, recommendRenewal: v })} />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.textLight }}>
                تقييم الشراكة (1-5)
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 10, background: C.bg, borderRadius: 8 }}>
                {[1,2,3,4,5].map(n => (
                  <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                    <Star size={20} fill={n <= form.rating ? C.accent : "none"} color={n <= form.rating ? C.accent : C.textLight} />
                  </button>
                ))}
                <span style={{ fontSize: 13, color: C.primary, fontWeight: 700 }}>{form.rating}/5</span>
              </div>
            </div>

            <Field label="ملاحظات" type="textarea" rows={2} placeholder="ملاحظات إضافية..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Handshake} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_PARTNER); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Demo Data
   ═════════════════════════════════════════════ */
const DEMO_PROJECTS = [
  { id: "fp1", name: "مشروع إفطار مليون صائم", category: "إفطار صائمين", description: "مشروع سنوي لتوفير وجبات إفطار في رمضان عبر 12 موقعاً بالمنطقة", target: 800000, collected: 680000, donors: 145, startDate: "1446/08/01", endDate: "1446/10/01", status: "active", responsible: "مدير تنمية الموارد", color: "#ea580c", notes: "يشمل إفطار المساجد وسكن العمال" },
  { id: "fp2", name: "مشروع بناء مسجد حي الياسمين", category: "بناء مساجد", description: "بناء مسجد جامع بسعة 500 مصلٍ في حي الياسمين الجديد", target: 2500000, collected: 1800000, donors: 62, startDate: "1445/06/01", endDate: "1447/06/01", status: "active", responsible: "مدير المساجد", color: "#0891b2", notes: "تم الانتهاء من الهيكل الإنشائي" },
  { id: "fp3", name: "كفالة 20 داعية", category: "كفالة دعاة", description: "توفير رواتب وبدلات 20 داعية يعملون في خدمة الجاليات", target: 720000, collected: 540000, donors: 38, startDate: "1446/01/01", endDate: "1446/12/30", status: "active", responsible: "مدير الجاليات", color: "#7c3aed", notes: null },
  { id: "fp4", name: "مشروع الصيانة الشاملة", category: "صيانة", description: "صيانة شاملة لـ 10 مساجد تشمل التكييف والدهان والسجاد", target: 500000, collected: 350000, donors: 28, startDate: "1446/03/01", endDate: "1446/09/01", status: "active", responsible: "مدير المساجد", color: "#16a34a", notes: "تم الانتهاء من 6 مساجد" },
  { id: "fp5", name: "برنامج المنح الدراسية", category: "برامج تعليمية", description: "تقديم 50 منحة دراسية لأبناء الجاليات في المرحلة الجامعية", target: 300000, collected: 120000, donors: 15, startDate: "1446/06/01", endDate: "1447/01/01", status: "planning", responsible: "مدير الجاليات", color: "#2563eb", notes: "في مرحلة التخطيط والتنسيق مع الجامعات" },
  { id: "fp6", name: "حملة الشتاء الدافئ", category: "مشاريع عامة", description: "توزيع 2000 حقيبة شتوية على العمال والمحتاجين", target: 150000, collected: 150000, donors: 85, startDate: "1446/04/01", endDate: "1446/05/30", status: "completed", responsible: "مدير التطوع", color: "#d97706", notes: "تم التوزيع بنجاح" },
];

const DEMO_PARTNERS = [
  { id: "pt1", name: "شركة النور التجارية", type: "راعي رئيسي", scope: "رعاية شاملة لبرامج إفطار الصائمين وملتقى الجاليات السنوي", value: 350000, startDate: "1446/01/01", endDate: "1446/12/30", status: "active", contactPerson: "أ. سلطان النور", color: "#ea580c", benefits: ["شعار على المطبوعات","تغطية إعلامية","مقاعد VIP","تقارير دورية"] },
  { id: "pt2", name: "مجموعة الراجحي الخيرية", type: "شريك استراتيجي", scope: "دعم مشاريع بناء المساجد وصيانتها في منطقة القصيم", value: 500000, startDate: "1445/01/01", endDate: "1447/01/01", status: "active", contactPerson: "أ. فهد الراجحي", color: "#0891b2", benefits: ["تقارير شفافية","زيارات ميدانية","تسمية مشاريع","شهادات تقدير"] },
  { id: "pt3", name: "بنك الإنماء", type: "راعي ذهبي", scope: "رعاية حفل تكريم المتطوعين ودعم برامج التدريب", value: 120000, startDate: "1446/01/01", endDate: "1446/12/30", status: "active", contactPerson: "أ. نايف العمري", color: "#7c3aed", benefits: ["شعار على اللوحات","دعوات فعاليات","تقارير أثر"] },
  { id: "pt4", name: "شركة المراعي", type: "داعم", scope: "توفير منتجات ألبان لبرنامج إفطار الصائمين", value: 80000, startDate: "1446/08/01", endDate: "1446/10/01", status: "active", contactPerson: "أ. خالد المراعي", color: "#16a34a", benefits: ["شعار على المواد","شهادة شكر"] },
  { id: "pt5", name: "مؤسسة الاتصالات السعودية", type: "راعي فضي", scope: "رعاية البنية التحتية التقنية والاتصالات", value: 60000, startDate: "1446/01/01", endDate: "1446/06/30", status: "expired", contactPerson: "أ. عادل التقني", color: "#475569", benefits: ["خدمات اتصال","شعار"] },
];

/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: 16 },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },
  searchBox: { display: "flex", alignItems: "center", gap: 8, background: C.bg, borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320 },
  searchInput: { border: "none", background: "transparent", outline: "none", fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%" },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.6 },
  detailBanner: { display: "flex", alignItems: "center", gap: 20, padding: 16, borderRadius: 12 },

  /* Projects */
  projectsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 },
  projectCard: { padding: 0, overflow: "hidden", cursor: "pointer" },
  projectStrip: { height: 4, width: "100%" },
  projectFinance: { display: "flex", justifyContent: "space-between", marginTop: 14, padding: "10px 0", borderTop: `1px solid ${C.border}` },
  financeRow: { display: "flex", flexDirection: "column", alignItems: "center", gap: 2 },
  progressBg: { height: 8, borderRadius: 4, background: C.bg, overflow: "hidden" },
  progressFill: { height: "100%", borderRadius: 4, transition: "width 0.5s" },

  /* Partners */
  partnersGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 },
  partnerCard: { padding: 20, cursor: "pointer" },
  partnerHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  partnerIcon: { width: 52, height: 52, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center" },
  partnerValue: { display: "flex", alignItems: "center", gap: 6, marginTop: 14, padding: "10px 0", borderTop: `1px solid ${C.border}` },
  partnerMeta: { display: "flex", gap: 12, marginTop: 8, fontSize: 12, color: C.textLight },
  benefitTag: { padding: "3px 8px", background: `${C.teal}10`, color: C.teal, borderRadius: 6, fontSize: 11, fontWeight: 500 },
};
